import React, { useState, useEffect } from 'react';
import { FaPlus, FaTrash } from 'react-icons/fa';
import { LANGUAGES } from '../constants';

const CausesEditor = ({ data, onUpdate, parameterName, activeLang, onLangChange }) => {
    // State mirroring the legacy 'causesType' and 'causesSubtype'
    const [causesType, setCausesType] = useState('');
    const [causesSubtype, setCausesSubtype] = useState('');
    // activeLang via props

    // Default causes list from legacy (Line 403)
    const defaultCauses = [
        { defaultCause: "critical low", colorCode: "danger" },
        { defaultCause: "too low", colorCode: "warning" },
        { defaultCause: "low", colorCode: "warning" },
        { defaultCause: "borderline", colorCode: "warning" },
        { defaultCause: "nearOptimal high", colorCode: "warning" },
        { defaultCause: "high", colorCode: "danger" },
        { defaultCause: "very high", colorCode: "danger" }
    ];

    // Helper: Check if cause already exists
    const availableCauses = data ? Object.keys(data) : [];
    const checkingAvailability = (type) => availableCauses.includes(type);

    // ACTION: Add new Cause Type
    const addCause = (type) => {
        const template = {
            heading: { en: "", hi: "", ar: "" },
            description: { en: "", hi: "", ar: "" },
            listType: "",
            list: [
                {
                    text: { en: "", hi: "", ar: "" },
                    image: "",
                },
            ],
        };

        onUpdate({
            ...data,
            [type]: template
        });
        setCausesType(type);
        setCausesSubtype('heading'); // Default to heading
    };

    // ACTION: Update Deeply Nested Content
    // ACTION: Update Deeply Nested Content
    const updateDeepContent = (val, path) => {
        // Path example: ['high', 'heading', 'en']
        if (!data && !path) return;

        const newData = data ? JSON.parse(JSON.stringify(data)) : {};
        let current = newData;

        for (let i = 0; i < path.length - 1; i++) {
            const key = path[i];
            // Look ahead to see if next key is number (implying array)
            const nextKey = path[i + 1];
            const isNextArray = typeof nextKey === 'number';

            if (!current[key]) {
                current[key] = isNextArray ? [] : {};
            }
            current = current[key];
        }
        current[path[path.length - 1]] = val;
        onUpdate(newData);
    };

    // ACTION: List Management (Add/Remove Tips)
    const addTipSection = () => {
        // Guard: Ensure causesType is valid and data[causesType] exists
        if (!causesType) return;

        const newData = JSON.parse(JSON.stringify(data || {}));

        // Ensure data[causesType] exists with a list array
        if (!newData[causesType]) {
            newData[causesType] = {
                heading: { en: "", hi: "", ar: "" },
                description: { en: "", hi: "", ar: "" },
                listType: "",
                list: []
            };
        }
        if (!Array.isArray(newData[causesType].list)) {
            newData[causesType].list = [];
        }

        newData[causesType].list.push({
            text: { en: "", hi: "", ar: "" },
            image: ""
        });
        onUpdate(newData);
    };

    // ACTION: Delete an entire cause type (e.g., remove the entire "high" block)
    const deleteCause = (type) => {
        if (!type || !data || !data[type]) return;

        const confirmed = window.confirm(
            `Are you sure you want to delete the "${type}" cause?\n\nThis will be permanent once you click Save Changes.`
        );
        if (!confirmed) return;

        const newData = JSON.parse(JSON.stringify(data || {}));
        delete newData[type];
        onUpdate(newData);

        // Reset local state so sub-editor closes cleanly
        setCausesType('');
        setCausesSubtype('');
    };

    const removeTipSection = (index) => {
        // Guard: Validate data, causesType, and index
        if (!data || !causesType) return;
        if (!data[causesType]) return;

        const newData = JSON.parse(JSON.stringify(data));

        // Ensure list exists and is an array
        if (!Array.isArray(newData[causesType].list)) return;

        // Validate index bounds
        if (index < 0 || index >= newData[causesType].list.length) return;

        // 1. Clear text for active language
        if (newData[causesType].list[index]?.text) {
            newData[causesType].list[index].text[activeLang] = "";
        }

        // 2. Check if all texts empty
        const currentItem = newData[causesType].list[index];
        const allEmpty = LANGUAGES.every(l => {
            const val = currentItem?.text?.[l.code];
            return !val || val.trim() === "";
        });

        // 3. Remove if safe
        if (allEmpty) {
            newData[causesType].list = newData[causesType].list.filter((_, idx) => idx !== index);
        }

        onUpdate(newData);
    };

    // RENDER: Sub-editors
    const renderSubEditor = () => {
        if (!causesType || !data[causesType]) return null;
        const currentData = data[causesType];

        return (
            <div className="pcr-sub-editor">
                <div style={{ marginBottom: '1rem' }}>
                    <label className="pcr-editor-label">Edit Property</label>
                    <select
                        className="pcr-nav-select w-100"
                        value={causesSubtype}
                        onChange={(e) => setCausesSubtype(e.target.value)}
                    >
                        <option value="" hidden>Select what to edit</option>
                        {Object.keys(currentData).map(k => (
                            <option key={k} value={k}>{k}</option>
                        ))}
                    </select>
                </div>

                {causesSubtype === 'heading' || causesSubtype === 'description' ? (
                    <div className="pcr-lang-section">
                        <div className="pcr-lang-tabs">
                            {LANGUAGES.map(lang => (
                                <div
                                    key={lang.code}
                                    className={`pcr-lang-tab ${activeLang === lang.code ? 'active' : ''}`}
                                    onClick={() => onLangChange(lang.code)}
                                >
                                    {lang.label}
                                </div>
                            ))}
                        </div>
                        <textarea
                            className="pcr-textarea"
                            value={currentData[causesSubtype][activeLang] || ''}
                            onChange={(e) => updateDeepContent(e.target.value, [causesType, causesSubtype, activeLang])}
                            placeholder={`Enter ${causesSubtype} (${activeLang})...`}
                        />
                    </div>
                ) : causesSubtype === 'list' ? (
                    <div>
                        <div className="pcr-lang-tabs">
                            {LANGUAGES.map(lang => (
                                <div
                                    key={lang.code}
                                    className={`pcr-lang-tab ${activeLang === lang.code ? 'active' : ''}`}
                                    onClick={() => onLangChange(lang.code)}
                                >
                                    {lang.label}
                                </div>
                            ))}
                        </div>
                        {(Array.isArray(currentData.list) ? currentData.list : []).map((item, idx) => (
                            <div key={idx} className="pcr-list-item-card">
                                <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-start' }}>
                                    <div style={{ flex: 1 }}>
                                        <label className="pcr-label-small">Tip Content</label>
                                        <textarea
                                            className="pcr-textarea-small"
                                            value={item?.text?.[activeLang] ?? ''}
                                            onChange={(e) => updateDeepContent(e.target.value, [causesType, 'list', idx, 'text', activeLang])}
                                        />
                                    </div>
                                    <div style={{ width: '150px' }}>
                                        <label className="pcr-label-small">Icon Name</label>
                                        <input
                                            type="text"
                                            className="pcr-input-small"
                                            value={item?.image ?? ''}
                                            onChange={(e) => updateDeepContent(e.target.value, [causesType, 'list', idx, 'image'])}
                                        />
                                    </div>
                                    <button
                                        className="pcr-btn-danger-icon"
                                        onClick={() => removeTipSection(idx)}
                                        title="Remove Tip"
                                    >
                                        <FaTrash />
                                    </button>
                                </div>
                            </div>
                        ))}
                        <button className="pcr-btn-secondary" onClick={addTipSection}>
                            <FaPlus /> Add Tip Section
                        </button>
                    </div>
                ) : (
                    <div className="pcr-generic-input">
                        {/* Fallback for simple strings like 'listType' */}
                        <input
                            className="pcr-input"
                            value={currentData[causesSubtype]}
                            onChange={(e) => updateDeepContent(e.target.value, [causesType, causesSubtype])}
                        />
                    </div>
                )}
            </div>
        );
    };

    return (
        <div>
            <h2 className="pcr-section-title">Causes Configuration</h2>

            {/* 1. Add/Select Logic */}
            <div className="pcr-causes-grid">
                {defaultCauses.map((cause) => (
                    <button
                        key={cause.defaultCause}
                        className={`pcr-cause-btn ${checkingAvailability(cause.defaultCause) ? 'active' : ''} ${cause.colorCode}`}
                        onClick={() => !checkingAvailability(cause.defaultCause) ? addCause(cause.defaultCause) : setCausesType(cause.defaultCause)}
                    >
                        {cause.defaultCause}
                        {checkingAvailability(cause.defaultCause) && <span className="pcr-check">✓</span>}
                    </button>
                ))}
            </div>

            {/* 2. Selector for Existing Causes */}
            {availableCauses.length > 0 && (
                <div style={{ margin: '1.5rem 0' }}>
                    <label className="pcr-editor-label">Select Cause to Edit</label>
                    <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                        <select
                            className="pcr-nav-select"
                            style={{ flex: 1 }}
                            value={causesType}
                            onChange={(e) => {
                                setCausesType(e.target.value);
                                setCausesSubtype('');
                            }}
                        >
                            <option value="" hidden>Choose a cause...</option>
                            {availableCauses.map(c => <option key={c} value={c}>{c}</option>)}
                        </select>
                        <button
                            className="pcr-btn-danger-icon"
                            onClick={() => deleteCause(causesType)}
                            disabled={!causesType}
                            title={causesType ? `Delete "${causesType}" cause` : 'Select a cause first'}
                            style={{ padding: '0.5rem 0.65rem', height: 'auto', width: 'auto', opacity: causesType ? 1 : 0.4, cursor: causesType ? 'pointer' : 'not-allowed' }}
                        >
                            <FaTrash />
                        </button>
                    </div>
                </div>
            )}

            {/* 3. The Editor */}
            {renderSubEditor()}
        </div>
    );
};

export default CausesEditor;
